import React, { useEffect, useState } from 'react';
import {
  AvailabilityCalendar,
  Button,
  createDateRange,
  EButtonType,
  getNumberOfDaysInConcern,
  KitIcons,
  parseToApiDate,
  SpinnerV2,
  stringifyNumber,
} from '@repo/apg-ui';
import { FormattedMessage, useIntl } from 'react-intl';
import './CalendarOverlay.scss';
import { Tab, Tabs } from 'react-bootstrap';
import classNames from 'classnames';
import { getDateRangeOfShortcut } from '../../../utils/calender';

interface Props {
  possibleBookingDates: { start: any; end: any; startdigital: any; mountainend: any };
  dateRanges: any;
  handleDateChange?: any;
  getAvailabilityForDigitalProduct: any;
  producturn: any;
  tags?: any;
  spotlength: number;
  timeslots: number[];
  productAvailability: any;
  setLoading: (key: string, value: boolean) => void;
  isLoading: boolean;
  isDisabled?: boolean;
  isDigital?: boolean;
}

export const CalendarOverlay = ({
  possibleBookingDates,
  dateRanges,
  handleDateChange,
  getAvailabilityForDigitalProduct,
  producturn,
  tags,
  spotlength,
  timeslots,
  productAvailability,
  setLoading,
  isLoading,
  isDisabled,
  isDigital = true,
}: Props) => {
  const [focusedRange, setFocusedRange] = React.useState<[number, number] | undefined>([0, 0]);
  const [newDateRanges, setNewDateRanges] = React.useState(dateRanges);
  const intl = useIntl();

  const labels = {
    weeks: 'weeks',
    selectWeeksAndChooseDate: 'selectWeeksAndChooseDate',
    available: intl.formatMessage({
      id: 'ec.journey02.available',
      defaultMessage: 'Verfügbar',
    }),
    notAvailable: intl.formatMessage({
      id: 'ec.journey02.unavailable',
      defaultMessage: 'Nicht verfügbar',
    }),
    disabled: intl.formatMessage({
      id: 'ec.journey02.disabled',
      defaultMessage: 'Ausserhalb buchbarem Zeitraum',
    }),
    note: 'note',
    crossYearErrorText: 'crossYearErrorText',
    days: intl.formatMessage({
      id: 'ec.general.days',
      defaultMessage: 'Tage',
    }),
    exceptionWeek: 'exceptionWeek',
  };
  const dateRangeShortcuts = [
    {
      key: 'summer_weeks',
      label: 'summer weeks',
      ...getDateRangeOfShortcut(
        'summer_weeks',
        possibleBookingDates?.startdigital?.date,
        possibleBookingDates?.end?.date,
      ),
    },
    {
      key: 'winter_weeks',
      label: 'winter weeks',
      ...getDateRangeOfShortcut(
        'winter_weeks',
        possibleBookingDates?.startdigital?.date,
        possibleBookingDates?.end?.date,
      ),
    },
    {
      key: '1_year',
      label: '1 year',
      ...getDateRangeOfShortcut('1_year', possibleBookingDates?.startdigital?.date, possibleBookingDates?.end?.date),
    },
  ];

  const [key, setKey] = useState<string>('period-tab-0');
  const [selectedShortcut, setSelectedShortcut] = useState<string>('weeks');

  const showAvailability = false;

  useEffect(() => {
    if (producturn) {
      if (isDigital) {
        setLoading('popupCalendar', true);
        getAvailabilityForDigitalProduct({
          producturn: producturn,
          startdate: parseToApiDate(dateRanges[0].startDate),
          duration: dateRanges[0].duration,
          timeslot: timeslots[0],
          spotlength: spotlength,
          shareoftime: 100,
        }).then(() => {
          setLoading('popupCalendar', false);
        });
      }
    }
  }, []);

  const dateChangeCallback = (newRange, newDuration, index) => {
    let fullRange = [newRange];
    let oldDateRanges = [...newDateRanges];
    oldDateRanges[index] = {
      range: fullRange,
      duration: newDuration,
      startDate: fullRange[0].startDate,
      endDate: fullRange[0].endDate,
    };

    setNewDateRanges([...oldDateRanges]);
    //handleDateChange([...oldDateRanges]);
  };

  const onDateChange = (startdate, item, index) => {
    // set time for dates because difference calculation is based on time
    const newDuration = getNumberOfDaysInConcern(
      item.selection.endDate?.setHours(23, 59, 59, 999),
      startdate?.setHours(0, 0, 0, 0),
    );

    const range = createDateRange(new Date(startdate), newDuration, {
      toEndOfYear: false,
      fullWeeks: false,
    });
    dateChangeCallback(range, newDuration, index);
  };

  const addAdditionPeriod = () => {
    let oldDateRanges = [...newDateRanges];

    let targetStartDate =
      (possibleBookingDates && possibleBookingDates.startdigital && possibleBookingDates.startdigital.date) || null;
    const range = createDateRange(new Date(targetStartDate), 7, {
      toEndOfYear: false,
      fullWeeks: false,
    });
    oldDateRanges.push({
      range: [range],
      duration: 7,
      startDate: range.startDate,
      endDate: range.endDate,
    });
    setNewDateRanges([...oldDateRanges]);
    //handleDateChange([...oldDateRanges]);
    setKey('period-tab-' + (oldDateRanges.length - 1));
  };

  const removeAdditionPeriod = (index) => {
    let oldDateRanges = [...newDateRanges];
    if (oldDateRanges.length && oldDateRanges[index]) {
      oldDateRanges.splice(index, 1);
      setNewDateRanges([...oldDateRanges]);
      //handleDateChange([...oldDateRanges]);
      setKey('period-tab-' + (oldDateRanges.length - 1));
    }
  };

  const getTabTitleText = (index) => {
    return (
      <div className='custom-label svg-button'>
        <span className='me-2'>{`${stringifyNumber(index + 1)} period`}</span>
        {newDateRanges.length > 1 && (
          <Button
            className='mt-0 p-0'
            buttonType={EButtonType.BasicLink}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              removeAdditionPeriod(index);
            }}
          >
            <KitIcons.CloseIcon />
          </Button>
        )}
      </div>
    );
  };
  const productHasSeasonTag = false; // tags?.bookingperiod?.includes("season");
  const isMountain = tags?.commspace?.includes('mountain');

  const mountainBookingDates = {
    ...possibleBookingDates,
    end: possibleBookingDates.mountainend,
  };

  console.log('isMountain', isMountain);
  console.log('mountainBookingDates', mountainBookingDates);

  const getShortcuts = (shortcut, index) => {
    if (
      tags?.bookingperiod &&
      !tags?.bookingperiod.includes('season') &&
      !tags?.bookingperiod.includes('year') &&
      !tags?.bookingperiod.includes('season_winter') &&
      !tags?.bookingperiod.includes('season_summer')
    ) {
      return null;
    } else if (
      tags?.bookingperiod &&
      shortcut.key === 'winter_weeks' &&
      !tags?.bookingperiod.includes('season') &&
      !tags?.bookingperiod.includes('season_winter')
    ) {
      return null;
    } else if (
      tags?.bookingperiod &&
      shortcut.key === 'summer_weeks' &&
      !tags?.bookingperiod.includes('season') &&
      !tags?.bookingperiod.includes('season_summer')
    ) {
      return null;
    } else if (tags?.bookingperiod && !tags?.bookingperiod.includes('year') && shortcut.key === '1_year') {
      return null;
    }
    return (
      <li className='nav-item'>
        {shortcut.disabled ? (
          <a
            className={classNames('nav-link disabled', {
              active: selectedShortcut === shortcut.key,
            })}
          >
            {shortcut.label}
          </a>
        ) : (
          <a
            className={classNames('nav-link cursor-pointer', {
              active: selectedShortcut === shortcut.key,
            })}
            onClick={() => {
              if (!shortcut.disabled) {
                setSelectedShortcut(shortcut.key);
                dateChangeCallback(shortcut.range, shortcut.duration, index);
              }
            }}
          >
            {shortcut.label}
          </a>
        )}
      </li>
    );
  };

  if (isDisabled) return null;

  return (
    <div className='overlay-content date-selection' id='root'>
      <div className='d-flex flex-column align-items-start justify-content-start gap-3 w-100 relative'>
        {isLoading && (
          <div className='loader'>
            <SpinnerV2 relative />
          </div>
        )}
        <div
          className={classNames('d-flex flex-column align-items-start justify-content-start gap-3 w-100', {
            disabled: isLoading,
          })}
        >
          {/* TODO: Hided tabs for now, this will be used in future, to show again remove 'd-none' class from Tabs component */}
          {newDateRanges && newDateRanges.length && (
            <Tabs
              id='controlled-tab-example'
              activeKey={key}
              onSelect={(k) => {
                if (k === 'addNew') {
                  addAdditionPeriod();
                } else {
                  setKey(k);
                }
              }}
              as={'div'}
              className='mb-3 d-none'
            >
              {newDateRanges.map((dateRange: any, index) => {
                return (
                  <Tab
                    as={'div'}
                    className={'tab-item text-capitalize'}
                    eventKey={`period-tab-` + index}
                    title={getTabTitleText(index)}
                  >
                    {tags && tags?.bookingperiod && tags?.bookingperiod.includes('season') && (
                      <div className='calender-shortcuts'>
                        <div className='shortcut-title d-flex' style={{ textTransform: 'none' }}>
                          <b>
                            <FormattedMessage id='ec.calendar.season.title' defaultMessage='Saisonales Produkt' />
                          </b>
                          :&nbsp;
                          <FormattedMessage
                            id='ec.calendar.season.description'
                            defaultMessage='unterschiedliche Preise im Sommer (KW 19-44) und Winter (KW 45-19)'
                          />
                        </div>
                        <div className='shortcut-item'>
                          <ul className='nav'>
                            <li className='nav-item'>
                              <a
                                className={classNames('nav-link cursor-pointer', {
                                  active: selectedShortcut === 'weeks',
                                })}
                                onClick={() => {
                                  setSelectedShortcut('weeks');
                                }}
                              >
                                weeks
                              </a>
                            </li>
                            {dateRangeShortcuts.map((shortcut: any) => {
                              return getShortcuts(shortcut, index);
                            })}
                          </ul>
                        </div>
                      </div>
                    )}

                    <div className='d-flex flex-column position-relative align-items-start justify-content-start'>
                      <AvailabilityCalendar
                        showAvailability={showAvailability}
                        availabilities={productAvailability}
                        dateRange={dateRange.range}
                        duration={dateRange.duration}
                        isMobile={false}
                        dragSelectionEnabled={false}
                        focusedRange={focusedRange}
                        onRangeFocusChange={(focus) => {
                          setFocusedRange(focus);
                        }}
                        labels={labels}
                        locale={intl.locale}
                        showWeekSelectionButtons={!productHasSeasonTag}
                        possibleBookingDates={isMountain ? mountainBookingDates : possibleBookingDates}
                        handleDateChange={(startdate, item) => {
                          onDateChange(startdate, item, index);
                          setSelectedShortcut('weeks');
                        }}
                        isAnalogProduct={!isDigital}
                      />
                    </div>
                  </Tab>
                );
              })}

              {/* TODO: Hided additional time periods button for now, this will be used in future, do not remove this code */}
              {/*<Tab*/}
              {/*    className="tab-item"*/}
              {/*    eventKey="addNew"*/}
              {/*    title={*/}
              {/*        <div className="add-new-button custom-label svg-button">*/}
              {/*            <KitIcons.PlusIcon />*/}
              {/*            <span className="ms-2">*/}
              {/*                Add additional period*/}
              {/*            </span>*/}
              {/*        </div>*/}
              {/*    }*/}
              {/*>*/}
              {/*    add additional period*/}
              {/*</Tab>*/}
            </Tabs>
          )}
        </div>

        <div className='d-flex flex-column align-items-end justify-content-start gap-3 w-100'>
          <Button
            className='applyToAll'
            buttonType={EButtonType.PillMedium}
            isDisabled={!newDateRanges.length}
            onClick={() => {
              handleDateChange(newDateRanges);
            }}
          >
            <KitIcons.CheckIcon />
            <span>
              <FormattedMessage id='ec.calendar.apply' defaultMessage='Anwenden' />
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
